"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api-client";

/**
 * Whether this user has objected to browser performance monitoring.
 *
 * The New Relic agent runs cookie-free — it stores nothing on the device — so it is not
 * gated on consent. It is disclosed and rests on legitimate interest, which carries a
 * right to object (GDPR art. 21). Monitoring is therefore on by default and this context
 * carries the objection, not a permission.
 *
 * `undefined` means not known yet: no session, or the lookup is still in flight or failed.
 * Nothing loads while it is unknown, so a user who objected is never monitored on the
 * strength of a request we could not complete.
 */
export type MonitoringPreference = boolean | undefined;

interface MonitoringPreferenceContextValue {
  /** True when the agent may run: the user is known and has not objected. */
  monitoringAllowed: boolean;
  /** True when the user has objected. `undefined` until known. */
  optedOut: MonitoringPreference;
  setOptedOut: (optedOut: boolean) => Promise<void>;
}

const MonitoringPreferenceContext = createContext<MonitoringPreferenceContextValue>({
  monitoringAllowed: false,
  optedOut: undefined,
  setOptedOut: async () => {},
});

export function MonitoringPreferenceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [optedOut, setOptedOutState] = useState<MonitoringPreference>(undefined);

  useEffect(() => {
    if (!user) {
      setOptedOutState(undefined);
      return;
    }
    let cancelled = false;

    api
      .getMonitoringPreference()
      .then((preference) => {
        if (!cancelled) setOptedOutState(preference.opted_out);
      })
      .catch(() => {
        // Unknown stays unknown, so nothing loads. Fail closed.
        if (!cancelled) setOptedOutState(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const setOptedOut = useCallback(async (value: boolean) => {
    const saved = await api.setMonitoringPreference(value);
    setOptedOutState(saved.opted_out);
  }, []);

  return (
    <MonitoringPreferenceContext.Provider
      value={{ monitoringAllowed: optedOut === false, optedOut, setOptedOut }}
    >
      {children}
    </MonitoringPreferenceContext.Provider>
  );
}

export function useMonitoringPreference(): MonitoringPreferenceContextValue {
  return useContext(MonitoringPreferenceContext);
}
